import React, { useState } from 'react';
import useSWR from 'swr';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, AlertCircle, Building2, Calendar, Receipt, FileSpreadsheet,
  ShoppingCart, Clock, LineChart, FileText, FileBarChart, Edit3, Loader2, Package,
  ShieldAlert,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { formatEuro, formatDate } from '@/lib/format';
import PageHeader from '@/components/PageHeader';
import EmptyState from '@/components/EmptyState';
import { Badge } from '@/components/ui/badge';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import { TEST_IDS } from '@/constants/testIds/alvora';
import { Button } from '@/components/ui/button';
import NewProjectDialog, { statusLabel } from '@/components/projects/NewProjectDialog';
import * as P from '@/lib/purchasing/service';

const fetchSummary = ([_, id]) => supabase.from('v_project_financial_summary').select('*').eq('project_id', id).maybeSingle().then(({ data, error }) => { if (error) throw error; return data; });
const fetchHasOrders = ([_, id]) => supabase.from('purchase_orders').select('id', { count: 'exact', head: true }).eq('project_id', id).neq('status', 'draft').then(({ count, error }) => { if (error) throw error; return (count || 0) > 0; });
const pct = (v) => (v == null ? null : `${Number(v).toFixed(1)}%`);

async function fetchProject([_, id, organisationId]) {
  if (!id || !supabase) return null;
  let query = supabase.from('projects').select('*').eq('id', id);
  if (organisationId) query = query.eq('organisation_id', organisationId);
  const { data, error } = await query.maybeSingle();
  if (error) throw error;
  return data;
}

import { ProjectTeam } from '@/components/employees/ComplianceAdmin';
import { ProgrammeTab } from '@/components/mywork/ProgrammeTab';
import { UnallocatedBadge } from '@/components/financial/UnallocatedPanel';

const TABS = [
  { value: 'overview', label: 'Overview', tid: TEST_IDS.tabOverview, active: true },
  { value: 'boq', label: 'BOQ & Budget', tid: TEST_IDS.tabBoq, active: true, icon: FileSpreadsheet, href: 'estimating' },
  { value: 'costs', label: 'Cost Control', tid: TEST_IDS.tabCosts, active: true, icon: LineChart, href: 'cost-control' },
  { value: 'packages', label: 'Packages', tid: 'tab-packages', active: true, icon: Package, href: 'packages' },
  { value: 'team', label: 'Team', tid: 'tab-team', active: true, icon: Clock },
  { value: 'purchasing', label: 'Purchasing', tid: TEST_IDS.tabPurchasing, active: true, icon: ShoppingCart, href: '../../purchasing' },
  { value: 'invoices', label: 'Supplier Invoices', tid: TEST_IDS.tabInvoices, active: true, icon: Receipt, href: '../../purchasing?tab=invoices' },
  { value: 'variations', label: 'Variations', tid: TEST_IDS.tabVariations, active: true, icon: Edit3, href: 'financial' },
  { value: 'programme', label: 'Programme', tid: TEST_IDS.tabProgramme, active: true, icon: Calendar },
  { value: 'documents', label: 'Portal & Drawings', tid: TEST_IDS.tabDocuments, active: true, icon: FileText, href: 'portal' },
  { value: 'safety', label: 'Safety', tid: 'tab-safety', active: true, icon: ShieldAlert, href: 'safety' },
  { value: 'reports', label: 'Financial Control', tid: TEST_IDS.tabReports, active: true, icon: FileBarChart, href: 'financial' },
];

function DetailRow({ label, value, dark }) {
  return (
    <div className={`flex items-start justify-between gap-4 py-2.5 border-b last:border-b-0 ${dark ? 'border-[#4A4946]' : 'border-border'}`}>
      <div className={`text-[11px] font-medium uppercase tracking-[0.14em] ${dark ? 'text-[#B5AFA5]' : 'text-muted-foreground'}`}>{label}</div>
      <div className={`text-sm text-right tabular-nums ${dark ? 'text-[#F7F5F1]' : 'text-charcoal'}`}>{value ?? '—'}</div>
    </div>
  );
}
const DarkCard = ({ title, children, testId }) => <div className="rounded-sm bg-[#3A3936] px-5 py-4 border-t border-[#957C5D]/60 shadow-[0_18px_40px_-28px_rgba(58,57,54,0.7)]" data-testid={testId}><h2 className="font-display text-base text-[#F7F5F1] mb-3 tracking-tight">{title}</h2>{children}</div>;

export default function ProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { organisationId, role } = useAuth();
  const isManager = ['owner', 'admin', 'manager'].includes(role || '');
  const { data: project, error, isLoading, mutate } = useSWR(
    id ? ['project', id, organisationId] : null,
    fetchProject
  );
  const summary = useSWR(isManager && id ? ['project-summary', id] : null, fetchSummary);
  const hasOrders = useSWR(isManager && id ? ['project-has-orders', id] : null, fetchHasOrders);
  const [editOpen, setEditOpen] = useState(false);
  const s = summary.data;

  if (isLoading) {
    return (
      <div className="p-8 flex items-center gap-2 text-muted-foreground">
        <Loader2 size={16} className="animate-spin" /> Loading project…
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 md:p-8 max-w-7xl mx-auto">
        <Link to="/projects" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-charcoal mb-4">
          <ArrowLeft size={14} /> Back to projects
        </Link>
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 flex items-start gap-2">
          <AlertCircle size={16} className="mt-0.5" />
          <span>Could not load project: {error.message}</span>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="p-6 md:p-8 max-w-7xl mx-auto">
        <Link to="/projects" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-charcoal mb-4">
          <ArrowLeft size={14} /> Back to projects
        </Link>
        <EmptyState
          icon={Building2}
          title="Project not found"
          description="This project may have been removed or you may not have permission to view it."
        />
      </div>
    );
  }

  return (
    <div data-testid={TEST_IDS.projectDetail} className="p-6 md:p-8 max-w-7xl mx-auto">
      <Link
        to="/projects"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-charcoal mb-4 transition-colors duration-150"
      >
        <ArrowLeft size={14} /> Back to projects
      </Link>

      <PageHeader
        title={project.name || 'Untitled project'}
        subtitle={
          <>
            <span className="font-mono text-xs">{project.project_code || '—'}</span>
            {project.client_name ? ` · ${project.client_name}` : ''}
          </>
        }
        actions={
          <>
            {project.status && <Badge data-testid="project-status-badge" variant="outline" className="text-[11px] uppercase tracking-wider">{statusLabel(project.status)}</Badge>}
            {isManager && <Button data-testid="project-edit-button" variant="outline" size="sm" onClick={() => setEditOpen(true)}><Edit3 size={14} className="mr-1.5" /> Edit project</Button>}
          </>
        }
      />
      {isManager && <NewProjectDialog open={editOpen} onOpenChange={setEditOpen} project={project} hasOrders={Boolean(hasOrders.data)} onCreated={() => { mutate(); summary.mutate(); }} />}

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="w-full justify-start overflow-x-auto flex-wrap bg-transparent p-0 h-auto border-b border-border rounded-none gap-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <TabsTrigger
                key={t.value}
                value={t.value}
                disabled={!t.active}
                data-testid={t.tid}
                onClick={() => {
                  if (t.active && t.href) {
                    navigate(t.href.startsWith('..') ? t.href.replace('../..', '') : `/projects/${id}/${t.href}`);
                  }
                }}
                className="data-[state=active]:bg-transparent data-[state=active]:text-bronze data-[state=active]:border-bronze data-[state=active]:shadow-none border-b-2 border-transparent rounded-none px-3 py-2 text-sm text-muted-foreground hover:text-charcoal disabled:text-muted-foreground/50 transition-colors duration-150"
              >
                {Icon && <Icon size={14} className="mr-1.5" />}
                {t.label}
                {!t.active && (
                  <span className="ml-2 text-[10px] uppercase tracking-wider rounded-full px-1.5 py-0.5 bg-muted text-muted-foreground">
                    Soon
                  </span>
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          {isManager && <div className="mb-4"><UnallocatedBadge projectId={id} /></div>}
          <div className={`grid grid-cols-1 ${isManager ? 'lg:grid-cols-3' : ''} gap-6`}>
            <div className="lg:col-span-2 rounded-lg border border-border bg-white px-5 py-4">
              <h2 className="font-display font-semibold text-base text-charcoal mb-3">
                Project details
              </h2>
              <div>
                <DetailRow label="Project name" value={project.name} />
                <DetailRow label="Project code" value={project.project_code} />
                <DetailRow label="Client" value={project.client_name} />
                {project.client_id && isManager && <DetailRow label="Client account" value={<Link to={`/finance/clients/${project.client_id}`} data-testid="project-client-account-link" className="text-[#6B4F3A] hover:underline">View client account →</Link>} />}
                <DetailRow label="Site address" value={project.site_address} />
                <DetailRow label="Project type" value={project.project_type} />
                <DetailRow label="Status" value={statusLabel(project.status)} />
                <DetailRow
                  label="Start date"
                  value={project.start_date ? formatDate(project.start_date) : null}
                />
                <DetailRow
                  label="Target finish"
                  value={project.target_finish_date ? formatDate(project.target_finish_date) : null}
                />
                <DetailRow
                  label="Created"
                  value={project.created_at ? formatDate(project.created_at) : null}
                />
              </div>
              {isManager && (
                <div className="mt-5 flex flex-wrap gap-2" data-testid="project-quick-links">
                  {[['Financial Control', 'financial', 'link-financial'], ['Cost Control', 'cost-control', 'link-cost-control'], ['Packages', 'packages', 'link-packages'], ['BOQ & Budget', 'estimating', 'link-estimating'], ['Portal & Drawings', 'portal', 'link-portal']].map(([l, h, t]) => (
                    <Link key={h} data-testid={t} to={`/projects/${id}/${h}`} className="text-xs rounded-full border border-border px-3 py-1 hover:border-bronze hover:text-bronze transition-colors">{l}</Link>
                  ))}
                  <Link data-testid="link-purchasing" to={"/purchasing"} className="text-xs rounded-full border border-border px-3 py-1 hover:border-bronze hover:text-bronze transition-colors">Purchasing</Link>
                  <Link data-testid="link-invoices" to={"/purchasing?tab=invoices"} className="text-xs rounded-full border border-border px-3 py-1 hover:border-bronze hover:text-bronze transition-colors">Supplier invoices</Link>
                </div>
              )}
            </div>

            {isManager && <div className="space-y-6">
              <DarkCard title="Contract summary">
                <div data-testid="contract-summary">
                <DetailRow dark label="Contract value (ex VAT)" value={project.contract_value_ex_vat != null ? formatEuro(project.contract_value_ex_vat) : null} />
                <DetailRow dark label="Contract VAT rate" value={project.contract_vat_rate != null ? `${Number(project.contract_vat_rate) * 100}%` : null} />
                <DetailRow dark label="Approved client variations" value={s ? formatEuro(s.approved_client_variations) : summary.isLoading ? '…' : null} />
                <DetailRow dark label="Current contract value" value={s ? formatEuro(s.current_contract_value) : summary.isLoading ? '…' : null} />
                </div>
                <Link data-testid="contract-edit-link" to={`/projects/${id}/financial`} className="mt-3 inline-block text-xs text-[#D8D0C4] hover:text-[#F7F5F1] underline-offset-4 hover:underline">Contract, PC sums, retention & client variations → Financial Control</Link>
              </DarkCard>

              {isManager && (
                <DarkCard title="Forecast" testId="forecast-summary">
                  {summary.error ? <div className="text-sm text-[#E8C9B8]">Could not load financial summary: {summary.error.message}</div> : (
                    <>
                      <DetailRow dark label="Current budget" value={s ? formatEuro(s.current_budget) : null} />
                      <DetailRow dark label="Committed" value={s ? formatEuro(s.committed_net) : null} />
                      <DetailRow dark label="Actual to date" value={s ? formatEuro(s.actual_net) : null} />
                      <DetailRow dark label="Forecast final cost" value={s ? formatEuro(s.forecast_final_net) : null} />
                      <DetailRow dark label="Forecast gross profit" value={s ? formatEuro(s.forecast_gross_profit) : null} />
                      <DetailRow dark label="Forecast gross margin" value={s ? pct(s.forecast_margin_pct) : null} />
                      {s && (s.invoices_awaiting_approval > 0 || s.orders_awaiting_approval > 0) && (
                        <div className="mt-3 text-xs text-[#D8D0C4]" data-testid="awaiting-approval">
                          {s.orders_awaiting_approval > 0 && <Link to={"/purchasing"} className="underline mr-3">{s.orders_awaiting_approval} order{s.orders_awaiting_approval === 1 ? '' : 's'} awaiting approval</Link>}
                          {s.invoices_awaiting_approval > 0 && <Link to={"/purchasing?tab=invoices"} className="underline">{s.invoices_awaiting_approval} invoice{s.invoices_awaiting_approval === 1 ? '' : 's'} awaiting approval</Link>}
                        </div>
                      )}
                      {s && !s.original_contract_value && <div className="mt-3 text-xs text-[#D8D0C4]" data-testid="contract-missing">Contract value not set — margin unavailable until the Owner confirms it in Financial Control.</div>}
                    </>
                  )}
                </DarkCard>
              )}
            </div>}
          </div>
        </TabsContent>
        <TabsContent value="team" className="mt-6"><ProjectTeam projectId={id} /></TabsContent>
        <TabsContent value="programme" className="mt-6"><ProgrammeTab projectId={id} /></TabsContent>
      </Tabs>
    </div>
  );
}
